import { updateTenant } from "../../api";
import {
  Title,
  TextInput,
  Button,
  Container,
  Center,
  Loader,
  Paper,
  Group,
  Text,
  Select,
  Code,
  NumberFormatter,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useLocation } from "wouter";
import LedgerTable from "./LedgerTable";
import AddCreditsForm from "./AddCreditsForm";
import CreditSettingsForm from "./CreditSettingsForm";
import { useTenantStore } from "./tenantStore";
import { IconRefresh } from "@tabler/icons-react";
import { useEffect } from "react";

export default function TenantEdit({ params }) {
  const [, setLocation] = useLocation();
  const tenant = useTenantStore((state) => state.tenants[params.id]);
  const fetchTenant = useTenantStore((state) => state.fetchTenant);
  const isLoading = useTenantStore((state) => state.isTenantLoading(params.id));
  const error = useTenantStore((state) => state.getTenantError(params.id));

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: tenant?.name || "",
      plan: tenant?.plan || "",
    },
  });

  // Fetch tenant data if needed when component mounts
  useEffect(() => {
    if (!tenant) {
      fetchTenant(params.id)
        .then((data) => {
          const values = {
            name: data.name || "",
            plan: data.plan || "",
          };
          form.setValues(values);
          form.setInitialValues(values);
        })
        .catch((err) => console.error("Failed to load tenant:", err));
    }
    // Mantine form object has unstable identity; including it would rerun this effect every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTenant, params.id, tenant]);

  if (isLoading || (!tenant && !error)) {
    return (
      <Container size="sm">
        <Center>
          <Loader mt="xl" />
        </Center>
      </Container>
    );
  }

  if (error || !tenant) {
    return (
      <Container size="sm">
        <Center>
          <Text c="red">{error}</Text>
          <Button onClick={() => fetchTenant(params.id)}>Try Again</Button>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="sm">
      <Group justify="space-between">
        <Title order={1}>{tenant.name}</Title>

        <Button
          leftSection={<IconRefresh />}
          onClick={() => fetchTenant(params.id, true)}
        >
          Refresh
        </Button>
      </Group>

      <Paper my="md" withBorder p="md">
        <Text>
          Credits:{" "}
          <NumberFormatter
            value={tenant.credits}
            thousandSeparator
            decimalScale={0}
          />
        </Text>
        {/* <CreditsTable credits={tenant.credits} /> */}

        <Title mt="md" order={4}>
          Adjust Credits
        </Title>
        <AddCreditsForm tenant={tenant} />
      </Paper>

      <Paper my="md" withBorder p="md">
        <Title order={4}>Low balance &amp; auto top-up</Title>
        <CreditSettingsForm tenant={tenant} />
      </Paper>

      <Paper my="md" withBorder p="md">
        <form
          onSubmit={form.onSubmit((values) => {
            updateTenant(params.id, values).then(() => {
              setLocation("/tenants");
            });
          })}
        >
          <TextInput
            label="Tenant Name"
            placeholder="ABC SAS"
            {...form.getInputProps("name")}
            key={form.key("name")}
            radius="md"
          />

          <Title mt="md" order={4}>
            Plan: {tenant.plan}
          </Title>
          <Select
            {...form.getInputProps("plan")}
            key={form.key("plan")}
            data={["Free", "Starter", "Growth", "Business", "Pro"]}
            description=" WARNING: Changing this ONLY changes the value in the database. It has no
        effect on factual credits, intervals, the Stripe plan, etc. and might
        get overwritten by the next webhook call. Only use this when you know
        what you're doing.
"
          />

          <Button mt="lg" type="submit">
            Save
          </Button>
        </form>
      </Paper>

      <Paper my="md" withBorder p="md">
        <Text>
          Stripe customer id:{" "}
          <Code>{tenant.claims?.stripeCustomerId ?? "NONE"}</Code>
        </Text>
      </Paper>

      <Paper my="xl">
        <Title order={3}>Last Credit Additions</Title>
        <LedgerTable ledger={tenant.ledgerAdditions} />
      </Paper>

      <Paper my="xl">
        <Title order={3}>Last Transactions</Title>
        <LedgerTable ledger={tenant.ledger} />
      </Paper>
    </Container>
  );
}
